if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Dawn waits 500ms after the main image settles before moving the thumbnails, so on phones the
        // strip trails the swipe. Highlight right away and let the strip follow the main row as it scrolls.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        const mainRow = this.elements.viewer.querySelector('[id^="Slider-"]');
        mainRow?.addEventListener('scroll', this.onViewerScroll.bind(this), { passive: true });
        mainRow?.addEventListener('touchstart', () => (this.pendingMediaId = null), {
          passive: true,
        });
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        // While the row travels to a tapped thumbnail, skip the slides it passes on the way.
        if (this.pendingMediaId) {
          if (mediaId !== this.pendingMediaId) return;
          this.pendingMediaId = null;
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.pendingMediaId = mediaId;
        window.clearTimeout(this.pendingTimer);
        this.pendingTimer = window.setTimeout(() => (this.pendingMediaId = null), 1500);
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            this.scrollSliderTo(activeMedia.parentElement, activeMedia.offsetLeft);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.followsViewer()) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.scrollSliderTo(this.elements.thumbnails.slider, thumbnail.offsetLeft);
      }

      // True when both rows scroll sideways (phones), so the thumbnail strip tracks the main row.
      followsViewer() {
        const main = this.elements.viewer.slider;
        const strip = this.elements.thumbnails?.slider;
        return !!main && !!strip && main.scrollWidth - main.clientWidth > 1 && strip.scrollWidth - strip.clientWidth > 1;
      }

      onViewerScroll() {
        if (this.followFrame) return;
        this.followFrame = window.requestAnimationFrame(() => {
          this.followFrame = null;
          this.followViewer();
        });
      }

      // Keep the thumbnail of the image in view centred in the strip, moving in step with the swipe.
      followViewer() {
        if (!this.followsViewer()) return;
        const main = this.elements.viewer.slider;
        const strip = this.elements.thumbnails.slider;
        const slides = [...main.children].filter((slide) => slide.dataset.mediaId && slide.offsetParent !== null);
        if (slides.length < 2) return;
        const step = slides[1].offsetLeft - slides[0].offsetLeft;
        if (step <= 0) return;
        const position = Math.min(Math.max((main.scrollLeft - slides[0].offsetLeft) / step, 0), slides.length - 1);
        const index = Math.floor(position);
        const stripLeft = strip.getBoundingClientRect().left - strip.scrollLeft;
        const centreOf = (slide) => {
          const thumb = slide && strip.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
          if (!thumb) return null;
          const rect = thumb.getBoundingClientRect();
          return rect.left - stripLeft + rect.width / 2;
        };
        const from = centreOf(slides[index]);
        const to = centreOf(slides[Math.min(index + 1, slides.length - 1)]) ?? from;
        if (from === null) return;
        const centre = from + (to - from) * (position - index);
        const target = Math.min(Math.max(centre - strip.clientWidth / 2, 0), strip.scrollWidth - strip.clientWidth);
        // Scroll snapping would pull the strip back to a thumbnail edge between frames.
        strip.style.scrollSnapType = 'none';
        strip.style.scrollBehavior = 'auto';
        strip.scrollLeft = target;
      }

      // Smooth scrolling of a scroll-snap row is sometimes dropped while the page is still loading
      // (seen on phones: tapping a thumbnail left the main image in place). If the row has not
      // moved shortly after asking, jump straight to the target instead.
      scrollSliderTo(slider, left) {
        if (!slider) return;
        const start = slider.scrollLeft;
        slider.scrollTo({ left, behavior: 'smooth' });
        window.setTimeout(() => {
          if (Math.abs(slider.scrollLeft - start) > 2 || Math.abs(slider.scrollLeft - left) <= 2) return;
          const previous = slider.style.scrollBehavior;
          slider.style.scrollBehavior = 'auto';
          slider.scrollLeft = left;
          slider.style.scrollBehavior = previous;
        }, 200);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
